import { createContext, useContext, useMemo, ReactNode, useCallback, useEffect, useState } from "react";
import { ConnectionProvider, WalletProvider as SolanaWalletProvider, useWallet as useSolanaWallet, useConnection } from "@solana/wallet-adapter-react";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import { SolflareWalletAdapter } from "@solana/wallet-adapter-solflare";
import { WalletModalProvider, useWalletModal } from "@solana/wallet-adapter-react-ui";
import { clusterApiUrl, LAMPORTS_PER_SOL } from "@solana/web3.js";
import { toast } from "@/hooks/use-toast";
import { setUser } from "@/lib/sentry";
import { logger } from "@/lib/logger";

// Import wallet adapter CSS
import "@solana/wallet-adapter-react-ui/styles.css";

interface WalletContextType {
  connected: boolean;
  connecting: boolean;
  address: string | null;
  balance: number;
  connect: () => void;
  disconnect: () => void;
  publicKey: string | null;
  network: string;
  switchNetwork: (network: string) => void;
  connectionError: string | null;
  walletError: string | null;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

function WalletContextProvider({ children }: { children: ReactNode }) {
  const { connection } = useConnection();
  const { publicKey, connected, connecting, disconnect: solanaDisconnect } = useSolanaWallet();
  const { setVisible } = useWalletModal();
  const [balance, setBalance] = useState(0);
  const [network, setNetwork] = useState("devnet");
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);

  const address = useMemo(() => {
    if (!publicKey) return null;
    const addr = publicKey.toBase58();
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
  }, [publicKey]);

  const fullAddress = useMemo(() => {
    return publicKey?.toBase58() ?? null;
  }, [publicKey]);

  // Fetch balance when connected
  useEffect(() => {
    if (!publicKey || !connection) {
      setBalance(0);
      return;
    }

    const fetchBalance = async () => {
      try {
        const bal = await connection.getBalance(publicKey);
        setBalance(bal / LAMPORTS_PER_SOL);
      } catch (error) {
        logger.error("Failed to fetch balance", { error, publicKey: publicKey?.toBase58() });
        setBalance(0);
      }
    };

    fetchBalance();

    // Subscribe to balance changes
    const subscriptionId = connection.onAccountChange(publicKey, (account) => {
      setBalance(account.lamports / LAMPORTS_PER_SOL);
    });

    return () => {
      connection.removeAccountChangeListener(subscriptionId);
    };
  }, [publicKey, connection]);

  const connect = useCallback(() => {
    setVisible(true);
    setWalletError(null);
  }, [setVisible]);

  const disconnect = useCallback(() => {
    solanaDisconnect();
    // Clear Sentry user context
    setUser({ id: '', username: '' });
    toast({
      title: "Wallet Disconnected",
      description: "Your wallet has been disconnected",
    });
  }, [solanaDisconnect]);

  const switchNetwork = useCallback((newNetwork: string) => {
    setNetwork(newNetwork);
    setConnectionError(null);
    toast({
      title: "Network Changed",
      description: `Switched to ${newNetwork} network`,
    });
  }, []);

  // Show toast on successful connection and set Sentry user context
  useEffect(() => {
    if (connected && address && fullAddress) {
      toast({
        title: "Wallet Connected",
        description: `Connected to ${address}`,
      });

      // Set user context in Sentry
      setUser({
        id: fullAddress,
        username: address,
      });
    }
  }, [connected, address, fullAddress]);

  return (
    <WalletContext.Provider
      value={{
        connected,
        connecting,
        address,
        balance,
        connect,
        disconnect,
        publicKey: fullAddress,
        network,
        switchNetwork,
        connectionError,
        walletError,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function WalletProvider({ children }: { children: ReactNode }) {
  // Use devnet for testing, mainnet-beta for production
  const [selectedNetwork, setSelectedNetwork] = useState(WalletAdapterNetwork.Devnet);
  const endpoint = useMemo(() => clusterApiUrl(selectedNetwork), [selectedNetwork]);

  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <SolanaWalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <WalletContextProvider>{children}</WalletContextProvider>
        </WalletModalProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
}
